import { useCallback, useMemo } from 'react';
import Immutable from 'immutable';
import moment from 'moment';
import { useSelector } from 'react-redux';

import type { ImmutableEntity, ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { DAYS_IN_WEEK } from '../config/constants';
import type { Registrations, RegistrationsState } from '../types';

export default function useDailyRegistrations(
  selectorRegistrations: string[] = ['registrations', 'registrations'],
  selectorProgramStartDate: string[] = ['registrations', 'programStartDate'],
) {
  const registrations = useSelector<RegistrationsState, ImmutableEntity<Registrations>>(
    (state) => (
      state.getIn(selectorRegistrations, Immutable.OrderedMap()) as ImmutableEntity<Registrations>
    ),
  );

  const programStartDate = useSelector<ImmutableRootState<Date | string>, Date | string>(
    (state) => state.getIn(selectorProgramStartDate),
  );

  const [
    registrationsGroupedByWeek,
    highestAmountOrGoal,
    registrationsToday,
  ] = useMemo(() => {
    // @ts-expect-error
    const dateKeys = [...registrations.keys()] as string[]; // eslint-disable-line
    const registrationTodayKey = dateKeys.find((date) => moment().isSame(date, 'day'));

    return [
      groupRegistrationsByWeek(registrations.toJS()),
      getHighestAmountOrGoal(registrations.toJS()),
      registrations.get(registrationTodayKey, null),
    ];
  }, [registrations]);

  const getRegistrationsByWeekNumber = useCallback((weekNumber: number) => (
    // Week number is provided as 1-index so we subtract 0 to make it 0-indexed
    registrationsGroupedByWeek[weekNumber - 1]
  ), [registrationsGroupedByWeek]);

  const getWeekNumberFromRegistrationDate = useCallback((date: Date | string) => {
    const currentWeekIndex = registrationsGroupedByWeek.findIndex(
      (registrationsForWeek) => dateExistsInWeek(registrationsForWeek, date),
    );

    return currentWeekIndex + 1;
  }, [registrationsGroupedByWeek]);

  const weeksBeforeProgramStartDate = useMemo(() => (
    registrationsGroupedByWeek
      .filter((registrationsForWeek) => (
        Object.keys(registrationsForWeek).every((date) => (
          new Date(date).setHours(0, 0, 0, 0) < new Date(programStartDate).setHours(0, 0, 0, 0)
        ))
      ))
      .map((registrationsForWeek, index) => index + 1)
  ), [programStartDate, registrationsGroupedByWeek]);

  const [currentWeekNumber, registrationsForCurrentWeek] = useMemo(() => {
    const currentWeekIndex = registrationsGroupedByWeek.findIndex((registrationsForWeek) => {
      const dateKeys = Object.keys(registrationsForWeek);
      return dateKeys.some((date) => moment().isSame(date, 'day'));
    });

    const weekNumber = currentWeekIndex - weeksBeforeProgramStartDate.length + 1;

    return [weekNumber, getRegistrationsByWeekNumber(currentWeekIndex + 1)];
  }, [
    getRegistrationsByWeekNumber,
    registrationsGroupedByWeek,
    weeksBeforeProgramStartDate.length,
  ]);

  return {
    registrations: registrations.toJS(),
    registrationsGroupedByWeek,
    registrationsForCurrentWeek,
    registrationsToday,
    highestAmountOrGoal,
    currentWeekNumber, // => only program weeks.
    weeksBeforeProgramStartDate,
    numberOfWeeks: registrationsGroupedByWeek.length,
    getHighestAmountOrGoal,
    getRegistrationsByWeekNumber,
    getWeekNumberFromRegistrationDate,
    groupRegistrationsByWeek,
  };
}

function getHighestAmountOrGoal(registrations: Registrations) {
  const dates = Object.keys(registrations);

  let highestAmount: number | null = null;
  let highestGoal: number | null = null;

  for (let i = 0; i < dates.length; i++) {
    const currentDay = registrations[dates[i]];

    if (currentDay == null) {
      break;
    }

    const { totalAmount, goal } = currentDay;

    if (totalAmount > highestAmount) {
      highestAmount = totalAmount;
    }

    if (goal > highestGoal) {
      highestGoal = goal;
    }
  }

  const highestAmountOrGoal = Math.max(highestAmount, highestGoal);

  return highestAmountOrGoal;
}

function groupRegistrationsByWeek(registrations: Registrations) {
  const dates = Object.keys(registrations);
  const groupedRegistrations: Registrations[] = [];
  const remainingDays = dates.length % DAYS_IN_WEEK;

  let currentWeek = 0;

  for (let i = 0; i < dates.length; i += 1) {
    const dateKey = dates[i];
    const isNewWeek = (i + 1) % DAYS_IN_WEEK === remainingDays;

    if (groupedRegistrations[currentWeek] == null) {
      groupedRegistrations[currentWeek] = {
        [dateKey]: registrations[dateKey],
      };
    } else {
      groupedRegistrations[currentWeek][dateKey] = registrations[dateKey];
    }

    if (isNewWeek) {
      currentWeek += 1;
    }
  }

  return groupedRegistrations;
}

function dateExistsInWeek(registrations: Registrations, date: Date | string) {
  return Object.keys(registrations).some((registrationDate) => moment(registrationDate).isSame(moment(date), 'day'));
}
